import { IconButton, Tooltip } from '@dixa/lib-shared-habla';
import { UseFloatingMenuReturn } from 'articleEditor/hooks/useFloatingMenu';
import { basicBlockActions } from 'articleEditor/utils/actions';
import cx from 'classnames';
import { captionGlobalStore } from 'lib-knowledge-document-structure/plate/caption';
import {
  findNodePath,
  select,
  setNodes,
  useEditableProps,
} from 'lib-knowledge-document-structure/plate/common';
import { useImage } from 'lib-knowledge-document-structure/plate/media';
import {
  Resizable,
  ResizableProvider,
} from 'lib-knowledge-document-structure/plate/resizable';
import { useMyEditorRef } from 'lib-knowledge-document-structure/plateHooks';
import {
  MyEditor,
  MyImageElement,
} from 'lib-knowledge-document-structure/plateTypes';
import { Path } from 'lib-knowledge-document-structure/slate/package';
import { useSelected } from 'lib-knowledge-document-structure/slate/react';
import { imageURLWithEditorWidth } from 'lib-knowledge-management-common/hooks/useUploadImage';
import { useMemo } from 'react';
import { mergeRefs } from 'react-merge-refs';

import { Caption } from '../../caption/caption';
import { ResizeHandle } from '../../resizeHandle';
import resizeStyles from '../../resizeHandle/resizeHandle.module.scss';
import { SortableBlockWrapper } from '../baseBlock';
import s from '../baseBlock/block.module.scss';

import { ImageBlockProps } from './imageBlock';
import imageStyles from './imageBlock.module.scss';

type ImageBlockValueProps = ImageBlockProps & {
  floatingUploadMenu: UseFloatingMenuReturn;
  floatingLinkMenu: UseFloatingMenuReturn;
};

export default function ImageBlockValue({
  element,
  attributes,
  children,
  floatingUploadMenu,
  floatingLinkMenu,
}: ImageBlockValueProps): JSX.Element {
  const editor = useMyEditorRef();
  const { readOnly } = useEditableProps();
  const selected = useSelected();
  const path = findNodePath(editor, element);
  if (!path) throw new Error('this cant happen, path should always be defined');
  const imgProps = useImage();

  const blockActions = useMemo(
    () =>
      createBlockActions({
        editor,
        element,
        openUploadMenu: floatingUploadMenu.openFloatingMenu,
        openLinkMenu: floatingLinkMenu.openFloatingMenu,
        path,
      }),
    [
      editor,
      element,
      floatingLinkMenu.openFloatingMenu,
      floatingUploadMenu.openFloatingMenu,
      path,
    ],
  );
  const { imgAlign = 'center' } = element;

  const imgAlignClass = cx({
    [s['blockAlignStretch']]: imgAlign === 'stretch',
    [s['blockAlignCenter']]: imgAlign === 'center',
    [s['blockAlignEnd']]: imgAlign === 'end',
    [s['blockAlignStart']]: imgAlign === 'start',
  });

  return (
    <ResizableProvider>
      <SortableBlockWrapper
        className={cx(
          s['blockImageValue'],
          resizeStyles['resizeContainer'],
          selected && resizeStyles['resizeContainerHoverFocused'],
        )}
        element={element}
        blockActions={blockActions}
      >
        <div {...attributes} className={s['blockContent']}>
          <figure
            ref={mergeRefs([
              floatingUploadMenu.floatingResult.refs.setReference,
              floatingLinkMenu.floatingResult.refs.setReference,
            ])}
            contentEditable={false}
            className={s['blockImageWrapper']}
          >
            <Resizable
              options={{
                align:
                  imgAlign === 'start'
                    ? 'left'
                    : imgAlign === 'end'
                      ? 'right'
                      : 'center',
                readOnly: readOnly || imgAlign === 'stretch',
              }}
              className={cx(
                s['blockImagePreview'],
                imgAlignClass,
                selected && s['focused'],
                readOnly && s['imageReadonly'],
              )}
            >
              {!readOnly && imgAlign !== 'stretch' && (
                <ResizeHandle side="left" />
              )}

              <LinkHelper
                element={element}
                onClick={floatingLinkMenu.openFloatingMenu}
              />

              <img
                src={imageURLWithEditorWidth(
                  imgProps.props.src,
                  element.originalWidth,
                )}
                draggable={false}
              />

              {!readOnly && imgAlign !== 'stretch' && (
                <ResizeHandle side="right" />
              )}
            </Resizable>

            <Caption align={imgAlign} />
          </figure>
          {children}
        </div>
      </SortableBlockWrapper>
    </ResizableProvider>
  );
}

function createBlockActions({
  editor,
  element,
  openUploadMenu,
  openLinkMenu,
  path,
}: {
  editor: MyEditor;
  element: MyImageElement;
  openUploadMenu: () => void;
  openLinkMenu: () => void;
  path: Path;
}) {
  return [
    ...basicBlockActions,
    {
      key: 'replace',
      icon: 'image' as const,
      label: 'Edit',
      isSelected: false,
      action: openUploadMenu,
    },
    {
      key: 'link',
      icon: 'link' as const,
      label: 'Link',
      isSelected: false,
      action: openLinkMenu,
    },
    {
      key: 'caption',
      icon: 'sms' as const,
      label: 'Caption',
      isSelected: false,
      action: () => {
        select(editor, path);
        setTimeout(() => {
          captionGlobalStore.set.focusEndCaptionPath(path);
        }, 0);
      },
    },
    {
      key: 'heading',
      label: 'Alignment',
    },
    {
      key: 'stretch',
      icon: 'align-justify',
      label: 'Stretch',
      isSelected: element.imgAlign === 'stretch',
      action: () => {
        setNodes(editor, { imgAlign: 'stretch' }, { at: path });
      },
    },
    {
      key: 'start',
      icon: 'align-left',
      label: 'Left',
      isSelected: element.imgAlign === 'start',
      action: () => {
        setNodes(editor, { imgAlign: 'start' }, { at: path });
      },
    },
    {
      key: 'end',
      icon: 'align-right',
      label: 'Right',
      isSelected: element.imgAlign === 'end',
      action: () => {
        setNodes(editor, { imgAlign: 'end' }, { at: path });
      },
    },
    {
      key: 'center',
      icon: 'align-center',
      label: 'Center',
      isSelected: element.imgAlign === 'center',
      action: () => {
        setNodes(editor, { imgAlign: 'center' }, { at: path });
      },
    },
  ] as const;
}

type LinkHelperProps = {
  element: MyImageElement;
  onClick: () => void;
};

function LinkHelper({ element, onClick }: LinkHelperProps): JSX.Element {
  if (!element || !element.linkUrl) return <></>;

  return (
    <Tooltip
      // @ts-expect-error - habla misses the style prop but it works
      style={{
        position: 'absolute',
        right: 4,
        top: 4,
      }}
      dark
      content={`Image links to ${element.linkUrl}`}
    >
      <IconButton
        className={imageStyles['imageLinkButton']}
        onClick={onClick}
        icon="link"
        variant="tertiary"
      ></IconButton>
    </Tooltip>
  );
}
